<template>
  <Container class="jcsb-container--fullheight">
    <ItemListPagination
      :columns="columns"
      :currentPageNum="currentPageNum"
      :isLoading="isListLoading"
      :itemComponent="$options.CommandQueueDetailRow"
      :rowsPerPage="rowsPerPage"
      :showEmptyState="showEmptyState"
      :sortBy="sortBy"
      :sortDirection="sortDirection"
      :tableData="getTableData"
      :totalCount="totalCount[0]"
      @pageChange="handlePageChange"
      @sortColumn="handleSortColumn"
    >
      <template v-slot:toolbar>
        <Grid
          :class="$style.toolbar"
          hasGap
        >
          <GridItem :gridSpan="3">
            <InputSearch
              :focus="focusSearch"
              placeholder="Search"
              :value="searchTerm"
              @input="handleEnterSearchTerm"
            />
          </GridItem>
          <GridItem :gridSpan="6" />
          <GridItem
            :class="$style.filterLabel"
            :gridSpan="1"
          >
            Filter by
          </GridItem>
          <GridItem :gridSpan="1">
            <Select
              :class="$style.select"
              :isDisabled="false"
              :isHelperTextMarkdown="false"
              :options="dropDownOptions"
              :value="dropDownValue"
              @input="value => handleDropDownChange(value)"
            />
          </GridItem>
          <GridItem
            :class="$style.countItemsLabel"
            :gridSpan="1"
          >
            {{ totalCount[1] }}
          </GridItem>
        </Grid>
      </template>
    </ItemListPagination>
    <HeadingSmall
      v-if="showEmptyState"
      :class="$style.noResults"
      hasMargin
    >
      There are no commands in the queue
    </HeadingSmall>
  </Container>
</template>

<script>
import {
  Constants,
  Container,
  Grid,
  GridItem,
  HeadingSmall,
  InputSearch,
  ItemListPagination,
  Select,
  Types,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { commandDevicesStatusMap, commandDevicesStatusTypes } from 'client/Commands/constants/CommandDevicesStatuses';
import { mapActions, mapGetters, mapState } from 'vuex';
import CommandQueueDetailRow from 'client/Commands/CommandQueueDetailRow.vue';
import moment from 'moment';
import pluralize from 'pluralize';

const { Column } = Types;
const { sortDirections } = Constants;
const {
  successful,
  failed,
  timeout,
  pending,
  running,
} = commandDevicesStatusTypes;

const commandQueueCollection = StoreModuleMap[ItemTypeConsts.commandQueue];

const defaultDropDown = { name: 'All', value: 'all' };
const currentPageNumDefault = 0;
const rowsPerPageDefault = 10;
const searchTermDefault = '';
const sortDirectionDefault = sortDirections.ascending;
const sortByDefault = 'displayName';

export default {
  name: 'CommandQueueDetailItemTable',
  components: {
    Container,
    HeadingSmall,
    ItemListPagination,
    InputSearch,
    Grid,
    GridItem,
    Select,
  },

  data() {
    return {
      isCollectionMapping: false,
      isListLoading: false,
      focusSearch: true,
      currentPageNum: currentPageNumDefault,
      rowsPerPage: rowsPerPageDefault,
      dropDownValue: defaultDropDown.value,
      sortField: `${sortDirectionDefault === sortDirections.ascending ? '' : '-'}${sortByDefault}`,
      searchTerm: searchTermDefault,
      sortDirection: sortDirectionDefault,
      sortBy: sortByDefault,
      customTotalCount: null,
      workflowInstanceId: this.$route.params.id,

      columns: [
        Column({
          label: 'Device',
          dataFieldName: 'displayName',
          sortable: true,
          width: '1fr',
        }),
        Column({
          label: 'Host Name',
          dataFieldName: 'hostname',
          sortable: true,
          width: '1fr',
        }),
        Column({
          label: 'Serial Number',
          dataFieldName: 'serialNumber',
          sortable: true,
          width: '1fr',
        }),
        Column({
          label: 'Last Contact',
          dataFieldName: 'lastContact',
          sortable: true,
          width: '1fr',
        }),
        Column({
          label: 'Status',
          dataFieldName: 'status',
          width: '1fr',
        }),
        Column({
          label: '',
          width: '1fr',
        }),
      ],

      dropDownOptions: [
        defaultDropDown,
        { name: commandDevicesStatusMap[successful].label, value: successful },
        { name: commandDevicesStatusMap[failed].label, value: failed },
        { name: commandDevicesStatusMap[timeout].label, value: timeout },
        { name: commandDevicesStatusMap[pending].label, value: pending },
        { name: commandDevicesStatusMap[running].label, value: running },
      ],
    };
  },

  CommandQueueDetailRow,

  computed: {
    ...mapState(commandQueueCollection, {
      stateCount: (state) => state.entities.items.totalCount,
    }),

    ...mapGetters(commandQueueCollection, [
      'getCollection',
      'getIsFetching',
    ]),

    getTableData() {
      const start = this.currentPageNum * this.rowsPerPage;
      const end = start + this.rowsPerPage;

      return this.mapCommandQueue(this.getCollection).slice(start, end);
    },

    showEmptyState() {
      return !this.totalCount[0]
        && !this.isListLoading
        && !this.isCollectionMapping
        && !this.getIsFetching;
    },

    totalCount() {
      const countState = this.customTotalCount === null ? this.stateCount : this.customTotalCount;
      const label = `${countState} ${pluralize('item', countState)}`;
      return [countState, label];
    },
  },

  mounted() {
    this.handleMount();
  },

  methods: {
    ...mapActions(commandQueueCollection, [
      'fetchAllCollectionCustom',
      'resetCollection',
    ]),

    dynamicSort(sortField) {
      let sortOrder = 1;
      let sortName = sortField;
      if (sortName[0] === '-') {
        sortOrder = -1;
        sortName = sortName.substr(1);
      }
      return (a, b) => {
        let result = 0;
        if (typeof (a[sortName] + b[sortName]) === 'number') {
          if (a[sortName] < b[sortName]) {
            result = -1;
          } else if (a[sortName] > b[sortName]) {
            result = 1;
          }
        }
        if (typeof (a[sortName] + b[sortName]) === 'string') {
          if (a[sortName].toLowerCase() < b[sortName].toLowerCase()) {
            result = -1;
          } else if (a[sortName].toLowerCase() > b[sortName].toLowerCase()) {
            result = 1;
          }
        }

        if (sortName === 'lastContact') {
          const unixA = moment(a[sortName]).valueOf();
          const unixB = moment(b[sortName]).valueOf();
          if (unixA < unixB) {
            result = -1;
          } else if (unixA > unixB) {
            result = 1;
          }
        }

        return result * sortOrder;
      };
    },

    handleDropDownChange(value) {
      this.dropDownValue = value;
      this.currentPageNum = currentPageNumDefault;

      if (this.dropDownValue === defaultDropDown.value) this.customTotalCount = null;
      else this.customTotalCount = this.mapCommandQueue(this.getCollection).length;
    },

    handleEnterSearchTerm(searchTerm) {
      if (searchTerm.length && !searchTerm.trim().length) {
        this.customTotalCount = null;
        return;
      }

      this.searchTerm = searchTerm;
      this.focusSearch = false;
      this.currentPageNum = currentPageNumDefault;

      this.customTotalCount = this.mapCommandQueue(this.getCollection).length;
    },

    async handleMount() {
      this.isListLoading = true;

      await this.resetCollection();
      await this.fetchAllCollectionCustom(this.workflowInstanceId);

      const commandQueues = this.getCollection;
      const commandId = commandQueues.length ? commandQueues[0].commandQueue.command : null;
      this.$emit('command', commandId);

      this.isListLoading = false;
    },

    handlePageChange({ currentPageNum, rowsPerPage }) {
      this.currentPageNum = currentPageNum;
      this.rowsPerPage = rowsPerPage;
    },

    handleSortColumn({ dataFieldName, sortDirection }) {
      this.sortField = `${this.sortDirection === sortDirections.ascending ? '-' : ''}${dataFieldName}`;
      this.sortBy = dataFieldName;
      this.sortDirection = sortDirection;
    },

    mapCommandQueue(commandQueue) {
      this.isCollectionMapping = true;
      const commandQueueMap = commandQueue
        .map(commandQ => commandQ)
        .sort(this.dynamicSort(this.sortField))
        .filter(commandQ => this.dropDownValue === defaultDropDown.value
            || this.dropDownValue === commandQ.status)
        .filter(commandQ => {
          if (this.searchTerm !== searchTermDefault) {
            const displayName = commandQ.displayName.toLowerCase().trim();
            const hostname = commandQ.hostname.toLowerCase().trim();
            const serialNumber = commandQ.serialNumber.toLowerCase().trim();
            const fixsearchTerm = this.searchTerm.toLowerCase().trim();

            return displayName.includes(fixsearchTerm)
              || hostname.includes(fixsearchTerm)
              || serialNumber.includes(fixsearchTerm);
          }
          return true;
        });

      this.isCollectionMapping = false;
      return commandQueueMap;
    },
  },
};
</script>

<style module>
.toolbar {
  align-items: center;
  display: flex;
}

.dropDownMenu {
  float: right;
  max-width: 2em;
}

.countItemsLabel {
  color: var(--jc-color-disabled-text);
}

.filterLabel {
  text-align: right;
}

.select {
  margin-top: 2em;
}

.noResults {
  margin-top: 4em !important;
  text-align: center;
  width: 100%;
}

</style>
